// Gedeelde woorden tussen de workspace en de beheervlakken (review ronde 2).
//
// Waarom een eigen bestand: de workspace toont het krediet ("Gesprekken") en de
// wachtrij toont wat een kredietverzoek vraagt ("Vraagt +20 gesprekken"). Staan
// die woorden op twee plekken, dan heet dezelfde soort op het ene scherm anders
// dan op het andere zodra iemand er één aanpast. Eén lijst, twee lezers.
//
// W6 ronde 3 (review): ook de wegingen van een bron, de herkomst ("vaste
// lijst") en de foutzin na een mislukte aanroep staan hier — dezelfde reden.

import { ApiError } from "../../lib/api";
import type { Gebruiker } from "../../contracts";

/** Hoe een kredietsoort op het scherm heet. Een soort die hier (nog) niet staat
 *  toont zijn sleutel — kaal maar eerlijk, en dan hoort hij hier bij. */
export const KREDIET_LABEL: Record<string, string> = {
  onderwerp: "Eigen onderwerpen",
  // W6: beantwoorde chatvragen per maand, geteld uit de tabel `gesprekken`.
  gesprek: "Gesprekken",
};

/** Het woord voor een kredietsoort, met de sleutel als terugval. */
export function kredietLabel(soort: string): string {
  return KREDIET_LABEL[soort] ?? soort;
}

// ── Gebruikersstatus (Gebruikers en Kosten & credits) ────────────────────────

/** Wat een status van een gebruiker betekent. Het vlak Gebruikers en de
 *  creditstabel tonen dezelfde pil; stond de uitleg op twee plekken, dan zei
 *  "uitgenodigd" op het ene scherm iets anders dan op het andere. */
export const STATUS_UITLEG: Record<Gebruiker["status"], string> = {
  uitgenodigd: "uitnodiging verstuurd, nog nooit ingelogd",
  actief: "heeft minstens één keer ingelogd",
  inactief: "toegang ingetrokken",
};

/** De uitleg bij een status; null bij een status die hier (nog) niet staat —
 *  dan staat het woord van de server er kaal, zonder verzonnen uitleg. */
export function statusUitleg(status: string): string | null {
  return (STATUS_UITLEG as Record<string, string>)[status] ?? null;
}

/** De reden op de plek van de rol als iemand er geen heeft. Geen rol is geldig
 *  zolang je beheerder bent (W9-besluit B3: admin is een vlag, geen rol), dus
 *  dan "beheert alleen" en geen streepje. Mensen, Kosten & credits en Gebruik
 *  tonen dezelfde woorden; stonden ze op drie plekken, dan liepen ze uit elkaar. */
export function geenRolReden(isAdmin: boolean): string {
  return isAdmin ? "beheert alleen" : "geen rol";
}

// ── Kosten & credits (W6 ronde 4) ────────────────────────────────────────────

/** Wat de server zonder prijs meldt (`prijzen.zonderPrijs[].wat`, de sleutels
 *  onder `credits:` in config/kosten.yaml), in gewone woorden. Eén lijst voor
 *  de prijzenuitleg én de kolomkoppen van de laantabel. Een sleutel die hier
 *  niet staat toont zichzelf — kaal maar eerlijk, en dan hoort hij hier bij. */
export const PRIJSLOOS_LABEL = {
  firecrawl: "Firecrawl",
  exa: "Exa",
  embedding: "Embeddings",
} as const satisfies Record<string, string>;

export type Prijsloos = keyof typeof PRIJSLOOS_LABEL;

/** Het woord voor iets zonder prijs, met de sleutel van de server als terugval. */
export function prijsloosLabel(wat: string): string {
  return (PRIJSLOOS_LABEL as Record<string, string>)[wat] ?? wat;
}

// ── Bronnen & dekking (W6 ronde 3) ───────────────────────────────────────────

/** Hoe een weging van een bron op het scherm heet. Eén lijst voor de
 *  tabeltitels, de knoppen, het toevoegformulier, de meldingen na een besluit
 *  en de opschoonronde: zo heet "hoog" nergens anders dan hier. */
export const WEGING_LABEL = {
  hoog: "hoog",
  normaal: "normaal",
  geblokkeerd: "geblokkeerd",
} as const satisfies Record<string, string>;

export type Weging = keyof typeof WEGING_LABEL;

/** Het woord voor een weging, met de sleutel als terugval (de opschoonronde
 *  draagt de weging als losse tekst). */
export function wegingLabel(weging: string): string {
  return (WEGING_LABEL as Record<string, string>)[weging] ?? weging;
}

/** Met hoofdletter, voor een titel ("Hoog · 2"). */
export function wegingTitel(weging: Weging): string {
  const w = WEGING_LABEL[weging];
  return w.charAt(0).toUpperCase() + w.slice(1);
}

/** Waar de weging van een bron vandaan komt. Het contract draagt de naam van
 *  het configuratiebestand ("sectors.yaml"); de beheerder leest "vaste lijst". */
export const HERKOMST_LABEL: Record<"besluit" | "sectors.yaml", string> = {
  besluit: "besluit",
  "sectors.yaml": "vaste lijst",
};

// ── Foutzinnen (Wachtrij en Bronnen & dekking) ───────────────────────────────

/**
 * Wat de beheerder leest als een aanroep mislukt.
 *
 * Alleen bij 400/404/409/422 is de tekst van de server een Nederlandse zin
 * over de invoer (bij een 422 met een lijst maakt lib/api er al een zin van).
 * Bij 401/403, een storing (5xx) of geen verbinding is zijn tekst technisch
 * ("database weg", "Internal Server Error"), dus dan een gewone zin. Een kale
 * statusregel ("400 Bad Request", als de server geen json stuurde) valt terug
 * op de zin van de aanroeper.
 */
export function leesbaar(err: unknown, anders: string): string {
  if (!(err instanceof ApiError)) return anders;
  const s = err.status;
  if (s === 400 || s === 404 || s === 409 || s === 422) {
    const zin = err.message.trim();
    return zin && !/^\d{3}\b/.test(zin) ? zin : anders;
  }
  if (s === 401 || s === 403) return "Geen toegang — log opnieuw in.";
  if (s === 0) return "Geen verbinding met de server — probeer het zo nog eens.";
  if (s >= 500) return "De server had even een storing — probeer het zo nog eens.";
  return anders;
}

/** Heeft nog eens proberen zin? Alleen bij geen verbinding of een storing; bij
 *  een 4xx zegt de server dat het zo niet kan, en dat verandert niet vanzelf. */
export function tijdelijk(err: unknown): boolean {
  return !(err instanceof ApiError) || err.status === 0 || err.status >= 500;
}

// ── Kwaliteit (W6 ronde 5, research §3.6) ────────────────────────────────────

// De drie toetsen van de steekproef (goed · twijfel · fout) staan hier bewust
// NIET: de server stuurt ze met hun woord mee (`toetsen[].label`, in de meters
// én in de lijst). Eén as, één woordenlijst, één plek — en die plek is de server.

/** De duim onder een chatantwoord (W6 ronde 1: 1, -1 of geen). Eén lijst voor
 *  de filterknoppen, de lijst en het detail van de chatlogs. */
export const DUIM_LABEL = {
  op: "duim omhoog",
  neer: "duim omlaag",
  geen: "geen duim",
} as const;

export function duimLabel(duim: -1 | 1 | null): string {
  return duim === 1 ? DUIM_LABEL.op : duim === -1 ? DUIM_LABEL.neer : DUIM_LABEL.geen;
}

/** Een gesprek dat mislukte: het filter en het woord op de badge. De zin van
 *  de server zelf ("De chat liep vast door een technische fout; …") staat
 *  alleen in het detail. */
export const FOUT_LABEL = { filter: "met fout", badge: "mislukt" } as const;

/** Wie er aan het woord is in een bewaard gesprek (`berichten[].role`). Een rol
 *  die hier niet staat, krijgt het neutrale woord "Bericht": de sleutel van de
 *  chat ("developer", …) zegt een beheerder niets. Dan hoort hij hier bij. */
export const BERICHT_ROL: Record<string, string> = {
  user: "Vraag",
  assistant: "Antwoord",
  system: "Instructie",
  tool: "Uitkomst van een stap",
};

export function berichtRol(rol: string | null): string {
  return (rol ? BERICHT_ROL[rol] : undefined) ?? "Bericht";
}

/** De drie gemiddelden van de chat-benchmark, in de volgorde van de rechter. */
export const BENCHMARK_MAAT = {
  juistheid: "juistheid",
  onderbouwing: "onderbouwing",
  nut: "nut",
} as const satisfies Record<string, string>;

export type BenchmarkMaat = keyof typeof BENCHMARK_MAAT;

// ── De meterkast (Spoor B, sessie B2) ────────────────────────────────────────

/** Wie een meter vult (`meter.wie` uit het contract), in gewone woorden plus
 *  wat het kost. Eén lijst voor de badge in de metertabel en voor de uitleg
 *  onder een bord; stonden ze op twee plekken, dan ging "model" op het ene
 *  scherm iets anders betekenen dan op het andere. */
export const WIE_LABEL = {
  auto: { kort: "automatisch", uitleg: "een script rekent dit uit, zonder kosten" },
  model: { kort: "model", uitleg: "hiervoor draait een taalmodel; dat kost geld" },
  owner: { kort: "mens", uitleg: "dit is een oordeel van een mens, geen meting" },
} as const satisfies Record<string, { kort: string; uitleg: string }>;

export function wieLabel(wie: string): string {
  return (WIE_LABEL as Record<string, { kort: string }>)[wie]?.kort ?? wie;
}

export function wieUitleg(wie: string): string | null {
  return (WIE_LABEL as Record<string, { uitleg: string }>)[wie]?.uitleg ?? null;
}

/** Hoe de drie vergelijkingen op het scherm heten, met de vraag die elk
 *  beantwoordt (ADR §1). De knop draagt het korte woord, de zin eronder de
 *  vraag — zodat een beheerder niet hoeft te weten wat "methode" hier betekent. */
export const VERGELIJK_LABEL = {
  periode: { kort: "Periode", vraag: "Wat deed de ronde? Deze maand naast de vorige." },
  methode: { kort: "Methode", vraag: "Is nieuw beter dan oud? Dezelfde maand, twee wegingen." },
  run: { kort: "Run", vraag: "Wat deed die knop? Deze meting naast de vorige." },
} as const satisfies Record<string, { kort: string; vraag: string }>;

/**
 * De sleutels die in het `detail` van een meting voorkomen, in gewone woorden
 * (de rijen achter een meter, B1 §11). Een sleutel die hier niet staat, toont
 * zichzelf met streepjes als spaties — kaal maar eerlijk, en dan hoort hij
 * hier bij. Nooit een ruwe `per_uitgeverstype` op het scherm.
 */
export const DETAIL_LABEL: Record<string, string> = {
  // waar de meting vandaan komt
  run: "Uit de oogstronde",
  rubric: "Beoordeeld met",
  maand: "Maand",
  peildatum: "Gemeten op",
  bereik: "Bereik",
  nulmeting: "Eerste meting",
  vorige_periode: "Vorige periode",
  reden: "Waarom er geen oordeel staat",
  // wie en waarom (bord 5)
  door: "Ingevoerd door",
  toelichting: "Toelichting",
  // bord 1, de oogst
  gemist: "Niet gevonden",
  per_config: "Per zoekopdracht",
  uniek_na_ontdubbeling: "Nieuw na ontdubbelen",
  zonder_nieuw_document: "Zonder nieuw document",
  exa_verzoeken: "Exa-verzoeken",
  llm_tokens_in: "Modeltokens erin",
  llm_tokens_uit: "Modeltokens eruit",
  // bord 2, het corpus
  skills: "Vaardigheden",
  documenten: "Documenten",
  gepubliceerd: "Gepubliceerd",
  trends_per_laag: "Trends per laag",
  per_uitgeverstype: "Per soort uitgever",
  mediaan_dagen: "Mediane leeftijd",
  ouder_dan_twee_jaar: "Ouder dan twee jaar",
  titelloos: "Zonder titel",
  definities: "Definities",
  zonder_crebo: "Zonder crebo-koppeling",
  dubbele_inhoud: "Dubbele inhoud",
  dubbele_skillnamen: "Dubbele namen van vaardigheden",
  rood: "Wat rood staat",
  // bord 3, het beeld
  eenbrons: "Steunt op één bron",
  datumloos: "Bewijs zonder datum",
  per_laag: "Per laag",
  // bord 4, de schermen
  per_type: "Per soort",
  per_vorm: "Per soort kaart",
  bronnen_per_uitgeverstype: "Bronnen per soort uitgever",
};

/** Het woord bij een detailsleutel; onbekend = de sleutel leesbaar gemaakt. */
export function detailLabel(sleutel: string): string {
  const bekend = DETAIL_LABEL[sleutel];
  if (bekend) return bekend;
  const los = sleutel.replace(/_/g, " ");
  return los.charAt(0).toUpperCase() + los.slice(1);
}

/** Sleutels die binnen een detail-verdeling voorkomen (de laag van een trend,
 *  de vorm van een editiekaart). Zelfde regel: onbekend toont zichzelf. */
export const DETAIL_WAARDE_LABEL: Record<string, string> = {
  // de laag-as (W15)
  drager: "draagt de trend",
  context: "onderbouwt",
  // de vormen van een editiekaart (S3)
  keten: "keten",
  citaat: "citaat",
  witte_vlek: "witte vlek",
  scenariokaart: "scenariokaart",
  vraag_aan_jou: "vraag aan jou",
  zwaar_rapport: "zwaar rapport",
  bevestigde_trend: "bevestigde trend",
  regelgevingsradar: "regelgevingsradar",
  regionale_afwijking: "regionale afwijking",
  rapport_aan_het_woord: "rapport aan het woord",
  vaardigheid_in_beweging: "vaardigheid in beweging",
};

export function detailWaardeLabel(sleutel: string): string {
  return DETAIL_WAARDE_LABEL[sleutel] ?? sleutel.replace(/_/g, " ");
}
